import React from 'react';
import { Clock } from 'lucide-react';

interface FermentationTimeSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  recommendedRange: string;
  targetBakeTime?: string;
  processType: 'cold-bulk' | 'pre-proofed';
}

export function FermentationTimeSlider({
  label,
  value,
  onChange,
  min,
  max,
  recommendedRange,
  targetBakeTime,
  processType
}: FermentationTimeSliderProps) {
  const formatDateTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getSessionTimes = () => {
    if (!targetBakeTime) return null;
    
    const bakeTime = new Date(targetBakeTime);
    
    if (processType === 'cold-bulk') {
      // For cold bulk, work backwards from bake time
      const shapingTime = new Date(bakeTime);
      shapingTime.setHours(shapingTime.getHours() - 2); // 2h proof
      
      const kneadingTime = new Date(shapingTime);
      kneadingTime.setHours(kneadingTime.getHours() - value); // Custom bulk time
      
      return {
        kneading: kneadingTime,
        shaping: shapingTime,
        baking: bakeTime
      };
    } else { // pre-proofed
      const shapingTime = new Date(bakeTime);
      shapingTime.setHours(shapingTime.getHours() - value); // Custom proof time
      
      const kneadingTime = new Date(shapingTime);
      kneadingTime.setHours(kneadingTime.getHours() - 3); // 3h bulk
      
      return {
        kneading: kneadingTime,
        shaping: shapingTime,
        baking: bakeTime
      };
    }
  };

  const times = getSessionTimes();

  return (
    <div className="p-4 bg-white/5 rounded-xl backdrop-blur-sm">
      <label className="block text-sm font-medium text-white/90">
        {label} ({value} hours)
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-runnable-track]:rounded-lg [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-400 [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-white/50 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-sm [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-amber-400 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:shadow-sm [&::-moz-range-progress]:bg-amber-400 [&::-moz-range-progress]:rounded-l-lg [&::-moz-range-progress]:h-2"
        style={{
          background: `linear-gradient(to right, rgb(251 191 36) 0%, rgb(251 191 36) ${(value - min) * 100 / (max - min)}%, rgba(255, 255, 255, 0.1) ${(value - min) * 100 / (max - min)}%, rgba(255, 255, 255, 0.1) 100%)`
        }}
      />
      <div className="flex justify-between text-xs text-white/50 mt-1">
        <span>{min}h</span>
        <span>{max}h</span>
      </div>
      <p className="text-sm text-white/70">
        Recommended: {recommendedRange}
      </p>
      
      {times && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-white/90 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            Estimated Timeline
          </h4>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Kneading:</span>
              <span className="text-white">{formatDateTime(times.kneading)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Shaping:</span>
              <span className="text-white">{formatDateTime(times.shaping)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white/70">Baking:</span>
              <span className="text-white">{formatDateTime(times.baking)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}